import React, { forwardRef } from "react";
import ReactSelect, { components } from "react-select";
import cn from "classnames";
import { Icon } from "assets/icons";

import { MediumText, RegularText } from "components/Text";

import styles from "./styles.module.scss";

const customStyles = {
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "var(--regularTextColor)",
    padding: 4,
  }),
  option: (provided, state) => ({
    ...provided,
    color: "var(--regularTextColor)",
    fontSize: 13,
    fontFamily: "RubikRegular",
    backgroundColor: state.isSelected
      ? "var(--secondaryColor)"
      : state.isFocused
        ? "var(--mainBackgroundColor)"
        : "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  control: (provided, state) => ({
    ...provided,
    minHeight: 30,
    borderColor: state.isFocused ? "var(--primaryColor)" : "var(--grey)",
    boxShadow: state.isFocused ? "0 0 0 2px var(--secondaryColor)" : "none",
    "&:hover": { borderColor: "var(--greyDark)" },
    cursor: "pointer",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "var(--regularTextColor)",
    fontSize: 13,
    fontFamily: "RubikRegular",
  }),
  input: (provided) => ({
    ...provided,
    fontSize: 13,
    fontFamily: "RubikRegular",
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: 13,
    color: "var(--greyDark)",
    fontFamily: "RubikRegular",
  }),
  menu: (provided) => ({ ...provided, zIndex: 30 }),
  menuPortal: (provided) => ({ ...provided, zIndex: 2000 }),
};

/**
 * Единый селект (react-select) с опциональным лейблом.
 */
export const Select = forwardRef(function Select(
  {
    label,
    labelWidth,
    labelVariant = "medium",
    labelSize,
    options = [],
    value,
    onChange,
    onInputChange,
    isDisabled,
    isSearchable = true,
    isClearable,
    isLoading,
    isMulti,
    className,
    containerClassName,
    placeholder = "",
    filterOption,
    width = 200,
    menuPlacement = "auto",
  },
  ref
) {
  // Не дублируем «:», если его передали вместе с лейблом
  const labelText = label ? `${String(label).replace(/:$/, "")}:` : "";
  const LabelComponent =
    labelVariant === "regular" ? RegularText : MediumText;

  return (
    <div ref={ref} className={cn(styles.container, containerClassName)}>
      {!!label && (
        <LabelComponent
          className={styles.label}
          size={labelSize}
          style={labelWidth ? { width: labelWidth, flexShrink: 0 } : undefined}
        >
          {labelText}
        </LabelComponent>
      )}
      <ReactSelect
        placeholder={placeholder}
        className={cn(styles.select, className)}
        classNamePrefix="select"
        value={value ?? null}
        isDisabled={isDisabled}
        isLoading={isLoading}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isMulti={isMulti}
        menuPlacement={menuPlacement}
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : undefined
        }
        menuPosition="fixed"
        closeMenuOnScroll={(ev) => {
          // Скролл внутри самого меню не должен его закрывать
          const cls =
            typeof ev.target?.className === "string" ? ev.target.className : "";
          return !cls.includes("select__menu");
        }}
        noOptionsMessage={() => "Ничего не найдено"}
        loadingMessage={() => "Загрузка..."}
        styles={{
          ...customStyles,
          container: (provided) => ({ ...provided, width }),
        }}
        components={{
          IndicatorSeparator: () => null,
          CrossIcon: () => <Icon type="close" />,
          DropdownIndicator: isDisabled
            ? () => null
            : components.DropdownIndicator,
        }}
        options={options}
        onInputChange={(val) => onInputChange?.(val)}
        filterOption={filterOption}
        onChange={onChange}
      />
    </div>
  );
});
